import type { AnyModel, Ref, RefConstructor } from "mobx-keystone";
import { standaloneAction } from "mobx-keystone";
import { isStoreName, nsS } from "../namespace";
import type { ModelNameFromStore } from "../namespace";
import type { TModel } from "./t-model";
import type { AnyStore } from "./t-store";

export interface RefPointer<T extends object> {
  /**
   * RefConstructor for the relational resource
   */
  ref: RefConstructor<T>;
  /**
   * the final name of the prop that you want to emulate. this should be the same as the id prop of that same resource minus "id". e.g. for an account with billerId and subscriberId we would supply "biller" and "subscriber" respectively
   */
  prop: string;
}

type IsCoercedDate<T, V> = [V] extends [Date]
  ? [T] extends [string]
    ? V
    : never
  : never;
type RemoveDateType<T> = Exclude<T, Date>;

export type ExtractModelValue<RawProp, ModelProp> = RawProp extends ModelProp
  ? ModelProp extends string | boolean | number | null | undefined
    ? RemoveDateType<ModelProp>
    : RawProp extends (infer S)[]
      ? S extends AnyModel
        ? Self<S>[]
        : S[]
      : RawProp extends { get maybeCurrent(): AnyModel }
        ? ModelProp extends { get maybeCurrent(): AnyModel }
          ? string
          : never
        : RawProp extends AnyModel
          ? ModelProp extends AnyModel
            ? Self<RawProp>
            : never
          : never
  : IsCoercedDate<Exclude<RawProp, null>, Exclude<ModelProp, null>>;

export type Self<T extends AnyModel> = {
  [K in keyof Pick<T, keyof T["$"]>]: T[K];
};

export type ExtendedStoreRefArrayKeys = "selectedForPayment";

export type RefArrayKey =
  | keyof {
      [K in keyof AnyStore as AnyStore[K] extends Ref<object>[]
        ? K extends symbol
          ? never
          : K extends "$modelId"
            ? never
            : K
        : never]: AnyStore[K] extends Ref<object>[] ? AnyStore[K] : never;
    }
  | ExtendedStoreRefArrayKeys;

type StoreWithRefArrays = Record<RefArrayKey, Ref<object>[]>;

export const inferModelType = <M extends string>(
  _storeName: M,
): ModelNameFromStore<M> | "NULL_POINTER" => {
  const parts = _storeName.split(nsS);
  // final part of this SHOULD be the store name
  const maybeStoreName = parts[parts.length - 1];

  if (isStoreName(maybeStoreName)) {
    const storeName = maybeStoreName;
    const baseModelName = storeName.split("Store")[0];
    return `${_storeName}${nsS}${baseModelName}` as ModelNameFromStore<M>;
  }

  return "NULL_POINTER" as const;
};

export const inferModelRef = <M extends string>(_storeName: M) => {
  return `${inferModelType(_storeName)}Ref` as `${ModelNameFromStore<M>}Ref`;
};

export const addToRefArray = standaloneAction(
  "tilliX/addToRefArray",
  <T extends TModel>(
    store: AnyStore,
    refArrayKey: RefArrayKey,
    item: T["id"] | T["$modelId"] | T,
  ) => {
    const ref = store.modelRef(item);

    if (store.handleReferenceCreationErrors(ref, `add_to${refArrayKey}`)) {
      return;
    }

    const storeArray = (store as unknown as StoreWithRefArrays)[refArrayKey];

    if (
      !storeArray.find(
        (r) =>
          (ref.maybeCurrent as TModel)?.id === (r.maybeCurrent as TModel)?.id,
      )
    ) {
      storeArray.push(ref);
      return true;
    }
    return false;
  },
);

export const removeFromRefArray = standaloneAction(
  "tilliX/removeFromRefArray",
  <T extends TModel>(
    store: AnyStore,
    refArrayKey: RefArrayKey,
    item: T["id"] | T["$modelId"] | T,
  ) => {
    const ref = store.modelRef(item);

    if (store.handleReferenceCreationErrors(ref, `remove_from${refArrayKey}`)) {
      return;
    }

    const storeArray = (store as unknown as StoreWithRefArrays)[refArrayKey];

    const refIndex = storeArray.findIndex((r) =>
      typeof item === "string"
        ? (r.maybeCurrent as TModel)?.id === item
        : (r.maybeCurrent as TModel)?.id === item.id,
    );
    if (~refIndex) {
      storeArray.splice(refIndex, 1);
      return true;
    }
    return false;
  },
);
